import { saveToCache } from './cacheService';
import { itemsAPI, batchesAPI } from './api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://pos.ai961.com/api';

/**
 * Sync all user data to local cache
 */
export const syncAllData = async () => {
  console.log('[Data Sync] Starting full data synchronization...');
  const token = localStorage.getItem('access_token');
  
  if (!token) {
    console.log('[Data Sync] No token found, skipping sync');
    return;
  }

  const headers = { Authorization: `Bearer ${token}` };
  const syncResults = {
    success: [],
    failed: [],
  };

  try {
    // 1. Sync Items
    try {
      console.log('[Data Sync] Fetching items...');
      const itemsResponse = await itemsAPI.getItems({ page_size: 10000 });
      const items = itemsResponse.data.results || itemsResponse.data || [];
      await saveToCache('ITEMS', items);
      syncResults.success.push(`Items: ${items.length}`);
      console.log(`[Data Sync] ✓ Items synced: ${items.length}`);
    } catch (error) {
      console.error('[Data Sync] ✗ Items sync failed:', error);
      syncResults.failed.push('Items');
    }

    // 2. Sync Batches
    try {
      console.log('[Data Sync] Fetching batches...');
      const batchesResponse = await batchesAPI.getBatches({ page_size: 10000 });
      const batches = batchesResponse.data.results || batchesResponse.data || [];
      await saveToCache('BATCHES', batches);
      syncResults.success.push(`Batches: ${batches.length}`);
      console.log(`[Data Sync] ✓ Batches synced: ${batches.length}`);
    } catch (error) {
      console.error('[Data Sync] ✗ Batches sync failed:', error);
      syncResults.failed.push('Batches');
    }

    // 3. Sync Categories
    try {
      console.log('[Data Sync] Fetching categories...');
      const categoriesResponse = await axios.get(`${API_URL}/items/categories/`, { headers });
      const categories = categoriesResponse.data.results || categoriesResponse.data || [];
      await saveToCache('CATEGORIES', categories);
      syncResults.success.push(`Categories: ${categories.length}`);
      console.log(`[Data Sync] ✓ Categories synced: ${categories.length}`);
    } catch (error) {
      console.error('[Data Sync] ✗ Categories sync failed:', error);
      syncResults.failed.push('Categories');
    }

    // 4. Sync Brands
    try {
      console.log('[Data Sync] Fetching brands...');
      const brandsResponse = await axios.get(`${API_URL}/items/brands/`, { headers });
      const brands = brandsResponse.data.results || brandsResponse.data || [];
      await saveToCache('BRANDS', brands);
      syncResults.success.push(`Brands: ${brands.length}`);
      console.log(`[Data Sync] ✓ Brands synced: ${brands.length}`);
    } catch (error) {
      console.error('[Data Sync] ✗ Brands sync failed:', error);
      syncResults.failed.push('Brands');
    }

    // 5. Sync Receipts
    try {
      console.log('[Data Sync] Fetching receipts...');
      const receiptsResponse = await axios.get(`${API_URL}/sales/`, { 
        headers,
        params: { page_size: 1000 }
      });
      const receipts = receiptsResponse.data.results || receiptsResponse.data || [];
      await saveToCache('RECEIPTS', receipts);
      syncResults.success.push(`Receipts: ${receipts.length}`);
      console.log(`[Data Sync] ✓ Receipts synced: ${receipts.length}`);
    } catch (error) {
      console.error('[Data Sync] ✗ Receipts sync failed:', error);
      syncResults.failed.push('Receipts');
    }

    // 6. Sync Low Stock Items
    try {
      console.log('[Data Sync] Fetching low stock items...');
      const lowStockResponse = await itemsAPI.getLowStockItems();
      const lowStockItems = lowStockResponse.data || [];
      await saveToCache('LOW_STOCK', lowStockItems);
      syncResults.success.push(`Low Stock: ${lowStockItems.length}`);
      console.log(`[Data Sync] ✓ Low stock items synced: ${lowStockItems.length}`);
    } catch (error) {
      console.error('[Data Sync] ✗ Low stock sync failed:', error);
      syncResults.failed.push('Low Stock');
    }

    // Log summary
    console.log('[Data Sync] ========== SYNC COMPLETE ==========');
    console.log('[Data Sync] ✓ Success:', syncResults.success.join(', '));
    if (syncResults.failed.length > 0) {
      console.log('[Data Sync] ✗ Failed:', syncResults.failed.join(', '));
    }
    console.log('[Data Sync] ========================================');

    // Save sync timestamp
    localStorage.setItem('last_data_sync', Date.now().toString());

    return syncResults;
  } catch (error) {
    console.error('[Data Sync] ✗ Fatal error during sync:', error);
    throw error;
  }
};

/**
 * Check if sync is needed (older than 1 hour)
 */
export const isSyncNeeded = () => {
  const lastSync = localStorage.getItem('last_data_sync');
  if (!lastSync) return true;

  const timeSinceSync = Date.now() - parseInt(lastSync);
  const oneHour = 60 * 60 * 1000;
  
  return timeSinceSync > oneHour;
};

/**
 * Auto-sync on network reconnection and after login
 */
export const setupAutoSync = () => {
  console.log('[Data Sync] Setting up auto-sync...');

  // Function to attempt sync if conditions are met
  const attemptSync = () => {
    const token = localStorage.getItem('access_token');
    if (token && navigator.onLine && isSyncNeeded()) {
      console.log('[Data Sync] Triggering sync (token available)');
      syncAllData().catch(err => {
        console.error('[Data Sync] Sync error:', err);
      });
    }
  };

  // Try initial sync
  attemptSync();

  // Monitor localStorage for token changes (login events)
  window.addEventListener('storage', (e) => {
    if (e.key === 'access_token' && e.newValue) {
      console.log('[Data Sync] Login detected - triggering sync');
      setTimeout(attemptSync, 1000);
    }
  });

  // Setup online event listener
  window.addEventListener('online', () => {
    console.log('[Data Sync] Network restored - triggering sync');
    setTimeout(attemptSync, 2000); // Wait 2 seconds for connection to stabilize
  });

  // Periodic sync every 30 minutes if online
  setInterval(() => {
    attemptSync();
  }, 30 * 60 * 1000); // 30 minutes
};

export default {
  syncAllData,
  isSyncNeeded,
  setupAutoSync,
};
